import * as XLSX from 'xlsx';
import _ from 'lodash';
import { format } from 'date-fns';
import { apiConstants, postGeneric } from '../common/constants';
import { bucketForServiceType } from '../components/dashboard/courierPerformance/performanceUtils';

// Service-type bucket → the short label shown after the courier parent (e.g. "Delhivery — Surface").
const SERVICE_LABEL = {
  SURFACE: 'Surface', AIR: 'Air', SDD: 'SDD',
  SAME_CITY_NDD: 'Same City NDD', INTERCITY_NDD: 'Inter-City NDD', REVERSE: 'Reverse',
};

// Courier master (prozoId → service type), fetched once and cached for the session, so the
// delay/lost feeds can show "parent — service type" like the order tabs. courierDetail carries
// the parent + prozoId but NOT the service type, which lives on the courier master.
let _courierServiceMapPromise = null;
async function getCourierServiceMap() {
  if (!_courierServiceMapPromise) {
    _courierServiceMapPromise = (async () => {
      const map = new Map();
      try {
        const res = await postGeneric(apiConstants.COURIER_LIST_CACHED, {
          andfilter: [{ field: 'is_deleted', operator: 'eq', value: '0' }],
          offset: 0, limit: 500, skipCount: true,
          includes: 'prozoId,parent,serviceTypePrimary,dispatchMode',
        });
        const list = res?.data?.data || res?.data || [];
        (Array.isArray(list) ? list : []).forEach((c) => {
          if (c.prozoId) map.set(c.prozoId, { serviceTypePrimary: c.serviceTypePrimary, dispatchMode: c.dispatchMode });
        });
      } catch (e) { /* leave empty — falls back to the parent name alone */ }
      return map;
    })();
  }
  return _courierServiceMapPromise;
}

// "parent — Service" from a courierDetail + the master service map. Falls back to just the
// parent (or name) when the service type can't be resolved.
function courierLabelFor(courierDetail, courierMap) {
  const cd = courierDetail || {};
  const parent = cd.parent || cd.name || '';
  if (!parent) return '';
  const st = courierMap.get(cd.prozoId);
  const bucket = st ? bucketForServiceType(st.serviceTypePrimary, st.dispatchMode) : null;
  const svc = bucket && SERVICE_LABEL[bucket] ? SERVICE_LABEL[bucket] : '';
  return svc ? `${parent} — ${svc}` : parent;
}

// Order-search projection to enrich the delay MIS. The CT freightsnapshot delay feed carries
// only the zone, so we pull pickup/drop details, invoice value and order status by AWB from
// order/search. IMPORTANT: this API's `includes` takes the Java (camelCase) field names — e.g.
// `pickupDetails`, NOT `pickup_details` — so we project the whole embedded objects here and
// read the (snake_case) response fields below. Using snake paths in `includes` projected
// nothing, which is why pickup/drop showed "—" and invoice showed ₹0.
const ADDR_INCLUDES = ['awbNumber', 'orderStatus', 'invoiceValue', 'pickupDetails', 'deliveryDetails', 'courierDetail'].join(',');

export async function fetchAddressesByAwbs(awbNumbers) {
  const map = {};
  const awbs = _.uniq((awbNumbers || []).filter(Boolean));
  if (!awbs.length) return map;
  const courierMap = await getCourierServiceMap();
  // Chunk so a large delay list doesn't produce one giant payload.
  const chunks = _.chunk(awbs, 200);
  for (const chunk of chunks) {
    try {
      const res = await postGeneric(apiConstants.ORDER_LISTS, {
        includes: ADDR_INCLUDES,
        andfilter: [{ field: 'awbNumber', operator: 'in', value: chunk }],
        orfilter: [],
        offset: 0,
        limit: chunk.length,
        skipCount: true,
      });
      const orders = res?.data?.data || [];
      orders.forEach((o) => {
        const awb = o.awb_number || o.awbNumber;
        if (!awb) return;
        const pu = o.pickup_details || o.pickupDetails || {};
        const dp = o.delivery_details || o.deliveryDetails || {};
        map[awb] = {
          fromName: pu.from_name || '',
          fromPincode: pu.from_pincode || '',
          fromCity: pu.from_city || '',
          fromState: pu.from_state || '',
          toName: dp.to_name || '',
          toPincode: dp.to_pincode || '',
          toCity: dp.to_city || '',
          toState: dp.to_state || '',
          invoiceValue: o.invoice_value ?? o.invoiceValue ?? null,
          orderStatus: o.orderStatus || o.order_status || '',
          courierLabel: courierLabelFor(o.courier_detail || o.courierDetail, courierMap),
        };
      });
    } catch (e) {
      // A failed chunk just leaves those rows' address blank — the rest still export.
    }
  }
  return map;
}

/**
 * Build and download the delay MIS with pickup + drop details.
 *
 * @param rows        the delay-shipment records (must have `awbNumber`)
 * @param baseColumns [{label, value}] — value is a field name (string) or (row)=>value
 * @param filename    base file name (a timestamp is appended)
 * The pickup/drop address columns are appended automatically, enriched by AWB.
 */
export async function downloadDelayMis({ rows = [], baseColumns = [], filename = 'Delayed Shipments', addrMap: preloaded }) {
  const data = Array.isArray(rows) ? rows : [];
  // Reuse the map the grid already loaded (no double fetch); fetch only if not supplied.
  const addrMap = preloaded || (await fetchAddressesByAwbs(data.map((r) => r.awbNumber)));
  // Show the courier as "parent — service type" (like the order tabs) wherever a caller
  // has a "Courier Partner" column, keeping it in its original position.
  const enrichedBase = baseColumns.map((c) =>
    c.label === 'Courier Partner'
      ? { ...c, value: (r) => addrMap[r.awbNumber]?.courierLabel || (typeof c.value === 'function' ? c.value(r) : r[c.value]) || '' }
      : c
  );
  const columns = [
    ...enrichedBase,
    { label: 'Order Status', value: (r) => addrMap[r.awbNumber]?.orderStatus || r.orderStatus || '' },
    { label: 'Pickup Name', value: (r) => addrMap[r.awbNumber]?.fromName || '' },
    { label: 'Pickup City', value: (r) => addrMap[r.awbNumber]?.fromCity || '' },
    { label: 'Pickup State', value: (r) => addrMap[r.awbNumber]?.fromState || '' },
    { label: 'Pickup Pincode', value: (r) => addrMap[r.awbNumber]?.fromPincode || '' },
    { label: 'Drop Name', value: (r) => addrMap[r.awbNumber]?.toName || '' },
    { label: 'Drop City', value: (r) => addrMap[r.awbNumber]?.toCity || '' },
    { label: 'Drop State', value: (r) => addrMap[r.awbNumber]?.toState || '' },
    { label: 'Drop Pincode', value: (r) => addrMap[r.awbNumber]?.toPincode || '' },
    // Enriched from order/search — the CT feed's own invoice_value is often 0.
    { label: 'Invoice Value', value: (r) => (addrMap[r.awbNumber]?.invoiceValue ?? r.invoice_value ?? 0) },
  ];
  const header = columns.map((c) => c.label);
  const jsonRows = data.map((r) =>
    columns.reduce((obj, c) => {
      obj[c.label] = typeof c.value === 'function' ? c.value(r) : (r[c.value] ?? '');
      return obj;
    }, {})
  );
  const ws = jsonRows.length
    ? XLSX.utils.json_to_sheet(jsonRows, { header })
    : XLSX.utils.aoa_to_sheet([header]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Shipments');
  const stamp = format(new Date(), 'dd-MMM-yyyy_HH-mm');
  XLSX.writeFile(wb, `${filename}_${stamp}.xlsx`);
}
